/* Cornea C11 landing page.
   1. Quote dialog and mobile quote button (nothing opens until a visitor clicks Request a Quote).
   2. Motion: scroll reveals, a pinned product story and a scroll-linked intro to the use cases.
   No libraries. Without JavaScript every Request a Quote link opens the enquiry form in the current tab and the whole page
   reads as a normal stacked layout. All motion is skipped when the visitor prefers reduced motion. */
(function () {
  'use strict';

  var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var hasObserver = 'IntersectionObserver' in window;

  var modal = document.getElementById('c11-quote-modal');
  var triggers = document.querySelectorAll('[data-c11-quote-trigger]');
  var sticky = document.querySelector('[data-c11-sticky]');
  var hero = document.getElementById('c11-hero');
  var finalSection = document.getElementById('c11-quote');
  var isOpen = false;

  /* =========================================================
     Frame loop: one place that reacts to scrolling and resizing.
     ========================================================= */
  var frameHandlers = [];
  var ticking = false;
  function runFrame() {
    ticking = false;
    for (var i = 0; i < frameHandlers.length; i++) { frameHandlers[i](); }
  }
  function schedule() {
    if (ticking) { return; }
    ticking = true;
    var done = false;
    var go = function () {
      if (done) { return; }
      done = true;
      runFrame();
    };
    // Prefer the next animation frame, but also set a short timer: animation frames are paused in background tabs,
    // and the update must never get stuck waiting for one.
    if (document.visibilityState === 'visible' && window.requestAnimationFrame) { window.requestAnimationFrame(go); }
    window.setTimeout(go, 60);
  }
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);

  /* =========================================================
     1. Quote dialog and mobile quote button
     ========================================================= */
  var dialog, closeBtn, frame, lastTrigger = null;
  var scrollLocked = false, savedRoot = null, savedBody = null, savedScrollX = 0, savedScrollY = 0;
  var LOCK_PROPS = ['overflow', 'overflow-x', 'overflow-y', 'padding-right'];
  var FOCUSABLE = 'a[href], button:not([disabled]), iframe, input, select, textarea, [tabindex]:not([tabindex="-1"])';

  function updateSticky() {
    if (!sticky || !hero) { return; }
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var heroGone = hero.getBoundingClientRect().bottom < 0;
    // Hide again once the final quote section comes into view, so the button never covers the footer.
    var finalReached = finalSection ? finalSection.getBoundingClientRect().top < vh : false;
    if (heroGone && !finalReached && !isOpen) { sticky.removeAttribute('hidden'); }
    else { sticky.setAttribute('hidden', ''); }
  }
  frameHandlers.push(updateSticky);

  if (modal && triggers.length) {
    dialog = modal.querySelector('[data-c11-dialog]');
    closeBtn = modal.querySelector('[data-c11-close]');
    frame = modal.querySelector('[data-c11-frame]');

    var focusables = function () {
      var list = dialog.querySelectorAll(FOCUSABLE);
      var out = [];
      for (var i = 0; i < list.length; i++) {
        if (list[i].offsetParent !== null || list[i] === document.activeElement) { out.push(list[i]); }
      }
      return out;
    };

    /* Scroll lock. The theme sets overflow-x: hidden on both <html> and <body> (assets/style.css), so the page
       scrolls on <html> and locking <body> alone is not enough. Both elements are locked, each one's own inline
       values are saved first and put back exactly on close, and the scroll position is kept. Scrolling inside the
       form iframe is a separate document and is not affected. */
    var saveInline = function (el) {
      var saved = {};
      for (var i = 0; i < LOCK_PROPS.length; i++) {
        var p = LOCK_PROPS[i];
        saved[p] = { value: el.style.getPropertyValue(p), priority: el.style.getPropertyPriority(p) };
      }
      return saved;
    };
    var restoreInline = function (el, saved) {
      var i, p;
      for (i = 0; i < LOCK_PROPS.length; i++) { el.style.removeProperty(LOCK_PROPS[i]); }
      for (i = 0; i < LOCK_PROPS.length; i++) {
        p = LOCK_PROPS[i];
        if (saved[p].value !== '') { el.style.setProperty(p, saved[p].value, saved[p].priority); }
      }
    };
    var lockScroll = function () {
      if (scrollLocked) { return; }
      var root = document.documentElement;
      var body = document.body;
      savedRoot = saveInline(root);
      savedBody = saveInline(body);
      savedScrollX = window.pageXOffset || root.scrollLeft || 0;
      savedScrollY = window.pageYOffset || root.scrollTop || 0;
      var gap = window.innerWidth - root.clientWidth;
      var basePad = parseFloat(window.getComputedStyle(body).paddingRight) || 0;
      root.style.setProperty('overflow', 'hidden');
      body.style.setProperty('overflow', 'hidden');
      if (gap > 0) { body.style.setProperty('padding-right', (basePad + gap) + 'px'); }
      scrollLocked = true;
    };
    var unlockScroll = function () {
      if (!scrollLocked) { return; }
      scrollLocked = false;
      restoreInline(document.documentElement, savedRoot);
      restoreInline(document.body, savedBody);
      savedRoot = null;
      savedBody = null;
      var nowX = window.pageXOffset || document.documentElement.scrollLeft || 0;
      var nowY = window.pageYOffset || document.documentElement.scrollTop || 0;
      if (nowX !== savedScrollX || nowY !== savedScrollY) { window.scrollTo(savedScrollX, savedScrollY); }
    };

    var openModal = function (trigger) {
      if (isOpen) { return; }
      isOpen = true;
      lastTrigger = trigger;
      // The form is only requested after a deliberate click, never on page load.
      if (frame && !frame.getAttribute('src')) { frame.setAttribute('src', frame.getAttribute('data-src')); }
      modal.removeAttribute('hidden');
      updateSticky();
      lockScroll();
      if (closeBtn) { closeBtn.focus(); } else { dialog.focus(); }
    };
    var closeModal = function () {
      if (!isOpen) { return; }
      isOpen = false;
      modal.setAttribute('hidden', '');
      unlockScroll();
      updateSticky();
      // Return focus to the link that opened the dialog without letting the browser scroll to it.
      if (lastTrigger && typeof lastTrigger.focus === 'function') { lastTrigger.focus({ preventScroll: true }); }
    };

    for (var t = 0; t < triggers.length; t++) {
      triggers[t].addEventListener('click', function (e) {
        // Let modified clicks (new tab, new window) keep their normal behaviour.
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) { return; }
        e.preventDefault();
        openModal(this);
      });
    }
    if (closeBtn) { closeBtn.addEventListener('click', closeModal); }
    var backdrop = modal.querySelector('[data-c11-backdrop]');
    if (backdrop) { backdrop.addEventListener('click', closeModal); }

    document.addEventListener('keydown', function (e) {
      if (!isOpen) { return; }
      var key = e.key || e.keyCode;
      if (key === 'Escape' || key === 'Esc' || key === 27) { e.preventDefault(); closeModal(); return; }
      if (key === 'Tab' || key === 9) {
        var items = focusables();
        if (!items.length) { e.preventDefault(); dialog.focus(); return; }
        var first = items[0];
        var last = items[items.length - 1];
        if (e.shiftKey && (document.activeElement === first || document.activeElement === dialog)) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
    // Keep keyboard focus inside the dialog while it is open.
    document.addEventListener('focusin', function (e) {
      if (isOpen && !dialog.contains(e.target)) { if (closeBtn) { closeBtn.focus(); } else { dialog.focus(); } }
    });
  }

  /* =========================================================
     2. Motion
     ========================================================= */
  function clamp(v, a, b) { return v < a ? a : (v > b ? b : v); }

  /* A pinned block: while its container scrolls past, the inner element is held in the viewport.
     This is done with position: fixed in JavaScript because the theme's overflow-x: hidden on <html> and <body>
     stops CSS position: sticky from working. It is still ordinary scrolling: nothing is hijacked. */
  function Pin(container, inner, fixedTop) {
    var state = '';
    var lastLeft = null;
    var lastWidth = null;
    this.update = function () {
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var r = container.getBoundingClientRect();
      var ih = inner.offsetHeight;
      var top = fixedTop === 'center' ? Math.max(16, Math.round((vh - ih) / 2)) : fixedTop;
      var st = r.top >= top ? 'top' : (r.bottom - ih > top ? 'fixed' : 'end');
      if (st === 'fixed') {
        if (state !== 'fixed' || lastLeft !== r.left || lastWidth !== r.width) {
          inner.classList.add('is-fixed');
          inner.classList.remove('is-end');
          inner.style.left = r.left + 'px';
          inner.style.width = r.width + 'px';
          inner.style.top = top + 'px';
          lastLeft = r.left;
          lastWidth = r.width;
        }
      } else if (st !== state) {
        inner.classList.remove('is-fixed');
        inner.classList.toggle('is-end', st === 'end');
        inner.style.left = '';
        inner.style.width = '';
        inner.style.top = '';
        lastLeft = null;
        lastWidth = null;
      }
      state = st;
      return clamp((top - r.top) / Math.max(1, r.height - ih), 0, 1);
    };
    this.reset = function () {
      inner.classList.remove('is-fixed');
      inner.classList.remove('is-end');
      inner.style.left = '';
      inner.style.width = '';
      inner.style.top = '';
      state = '';
      lastLeft = null;
      lastWidth = null;
    };
  }

  function initMotion() {
    var roots = document.querySelectorAll('.c11');
    var i;

    // Scroll-driven pieces run only on large screens when motion is allowed. Everything else is the plain stacked layout.
    var bigScreen = window.matchMedia ? window.matchMedia('(min-width: 992px)') : { matches: false };
    var story = document.querySelector('[data-c11-story]');
    var bridge = document.querySelector('[data-c11-bridge]');
    var storyPin = null, bridgePin = null;
    var steps = story ? story.querySelectorAll('.c11-step') : [];
    var layers = story ? story.querySelectorAll('.c11-layer') : [];
    var dots = story ? story.querySelectorAll('.c11-story__dots li') : [];
    var idxItems = bridge ? bridge.querySelectorAll('.c11-bridge__idx li') : [];
    var tiles = bridge ? bridge.querySelectorAll('.c11-tile') : [];
    var activeScene = -1;

    if (story) {
      var rail = story.querySelector('[data-c11-pin]');
      var stage = story.querySelector('[data-c11-pin-inner]');
      if (rail && stage) { storyPin = new Pin(rail, stage, 'center'); }
    }
    if (bridge) {
      var bInner = bridge.querySelector('[data-c11-pin-inner]');
      if (bInner) { bridgePin = new Pin(bridge, bInner, 0); }
    }

    function setScene(n) {
      if (n === activeScene) { return; }
      activeScene = n;
      for (var k = 0; k < layers.length; k++) { layers[k].classList.toggle('is-active', k === n); }
      for (k = 0; k < steps.length; k++) { steps[k].classList.toggle('is-active', k === n); }
      for (k = 0; k < dots.length; k++) { dots[k].classList.toggle('is-on', k <= n); }
    }

    function applyMode() {
      var on = bigScreen.matches && !reduceMotion;
      if (story) {
        story.classList.toggle('is-pin', on);
        if (!on && storyPin) { storyPin.reset(); }
        if (!on) { activeScene = -1; for (var k = 0; k < steps.length; k++) { steps[k].classList.remove('is-active'); } }
      }
      if (bridge) {
        bridge.classList.toggle('is-pin', on);
        if (!on) {
          if (bridgePin) { bridgePin.reset(); }
          bridge.style.removeProperty('--p');
          for (var j = 0; j < idxItems.length; j++) { idxItems[j].classList.remove('is-on'); }
          for (j = 0; j < tiles.length; j++) { tiles[j].classList.remove('is-on'); }
        }
      }
      runPinned();
    }

    function runPinned() {
      var on = bigScreen.matches && !reduceMotion;
      if (!on) { return; }
      var vh = window.innerHeight || document.documentElement.clientHeight;
      if (storyPin && story.classList.contains('is-pin')) {
        storyPin.update();
        // The scene is the last step whose top has passed 55% of the viewport.
        var n = 0;
        for (var k = 0; k < steps.length; k++) {
          if (steps[k].getBoundingClientRect().top < vh * 0.55) { n = k; }
        }
        setScene(n);
      }
      if (bridgePin && bridge.classList.contains('is-pin')) {
        var p = bridgePin.update();
        bridge.style.setProperty('--p', p.toFixed(4));
        for (var j = 0; j < idxItems.length; j++) { idxItems[j].classList.toggle('is-on', p >= 0.45 + j * 0.14); }
        for (j = 0; j < tiles.length; j++) { tiles[j].classList.toggle('is-on', p >= 0.3 + j * 0.15); }
      }
    }

    if (reduceMotion || !hasObserver) { return; }

    for (i = 0; i < roots.length; i++) { roots[i].classList.add('is-js'); }



    // Reveal on scroll. Elements are visible by default; this class is only added when JavaScript runs and motion is allowed.
    var reveal = new IntersectionObserver(function (entries) {
      for (var e = 0; e < entries.length; e++) {
        if (entries[e].isIntersecting) { entries[e].target.classList.add('is-in'); reveal.unobserve(entries[e].target); }
      }
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });
    var items = document.querySelectorAll('.c11-reveal');
    for (i = 0; i < items.length; i++) { reveal.observe(items[i]); }

    // Sections that start something when they come into view (the connectivity graphic).
    var inview = new IntersectionObserver(function (entries) {
      for (var e = 0; e < entries.length; e++) {
        if (entries[e].isIntersecting) { entries[e].target.classList.add('is-in'); inview.unobserve(entries[e].target); }
      }
    }, { threshold: 0.25 });
    var watch = document.querySelectorAll('[data-c11-inview]');
    for (i = 0; i < watch.length; i++) { inview.observe(watch[i]); }

    // The hero's slow background drift pauses once the hero has scrolled out of view.
    if (hero) {
      var heroWatch = new IntersectionObserver(function (entries) {
        hero.classList.toggle('is-off', !entries[0].isIntersecting);
      }, { threshold: 0 });
      heroWatch.observe(hero);
    }

    frameHandlers.push(runPinned);
    if (bigScreen.addEventListener) { bigScreen.addEventListener('change', applyMode); }
    else if (bigScreen.addListener) { bigScreen.addListener(applyMode); }
    applyMode();
  }

  initMotion();
  updateSticky();
})();
